import { useMemo } from 'react'

import { useGamificationPages } from '@/shared/lib/api/gamification-pages'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import type { GamificationPage } from '@/types/gamificationPage'
import type { GamificationPageBlock } from '@/types/gamificationPageBlock'

import { getBlockRegistryEntry, isKnownBlockKind } from '../blocks/registry'

/**
 * Blocos renderizáveis da home, lidos do snapshot versionado
 * (`/gamification/data` → `useGamificationDataStore`), não do REST.
 *
 * Descarta, nesta ordem:
 *  - bloco de `kind` desconhecido pelo registry local — a api pode ter um bloco
 *    que esta versão do app ainda não sabe desenhar;
 *  - bloco cujo módulo exigido está desativado no menu do evento.
 */
export const useHomeBlocks = (): { blocks: GamificationPageBlock[] } => {
  const getActiveProductData = useGamificationDataStore(
    state => state.getActiveProductData
  )
  const dataByProductId = useGamificationDataStore(state => state.dataByProductId)
  const activeDigitalProductId = useGamificationDataStore(
    state => state.activeDigitalProductId
  )

  const { records: pages } = useGamificationPages() as {
    records: GamificationPage[]
  }

  const homeBlocks = useMemo(
    () => getActiveProductData().home_blocks ?? [],
    // `dataByProductId` e `activeDigitalProductId` são as dependências reais:
    // `getActiveProductData` é estável e lê o estado no momento da chamada.
    [getActiveProductData, dataByProductId, activeDigitalProductId]
  )

  const activeModuleTypes = useMemo(() => {
    const active = (pages ?? []).filter(page => page.active && page.module_type)
    return new Set(active.map(page => page.module_type as string))
  }, [pages])

  const blocks = useMemo(
    () =>
      homeBlocks.filter(block => {
        if (!isKnownBlockKind(block.kind)) return false

        const requires = getBlockRegistryEntry(block.kind)?.requires
        if (!requires) return true

        // Sem páginas carregadas ainda, não escondemos nada — o menu chega logo
        // depois e o bloco some sozinho se o módulo estiver mesmo desativado.
        if (!pages || pages.length === 0) return true

        return activeModuleTypes.has(requires)
      }),
    [homeBlocks, activeModuleTypes, pages]
  )

  return { blocks }
}
